/**
 * INBOUND adapter — the read-back mirror of `legacyToV2Order` (orderV2.ts).
 *
 * Reconstruct the trader-native `{side, action}` pair from a Kalshi order as it
 * reads back, for DISPLAY. V2 is single-book: a buy-NO is sent as an `ask` (sell
 * YES), so on read-back it carries `side:"yes", action:"sell"` while
 * `outcome_side:"no"`. The canonical trader side is therefore **`outcome_side`**.
 *
 * Verified against live Kalshi reads 2026-06-19 (fixture
 * `relay/src/kalshi/__fixtures__/orderReadV2.capture.json`):
 *   - buy YES @ 1¢       → {side:"yes", action:"buy",  outcome_side:"yes"}
 *   - native buy NO @ 1¢ → {side:"no",  action:"buy",  outcome_side:"no"}
 *   - dash  buy NO @ 2¢  → {side:"yes", action:"sell", outcome_side:"no"}  ← was mislabeled YES
 *
 * We keep `{side, action}` a COHERENT trader-native pair (the invariant the
 * order card / queue / cost math relies on): when the order was placed cross-
 * framed (legacy `side` != `outcome_side`, i.e. a NO expressed on the YES leg),
 * the legacy `action` is also YES-framed, so we flip it. Otherwise the legacy
 * pair is already trader-native. This is the *correct* form of "use
 * outcome_side" — unlike reading `outcome_side` for `side` alone, it does NOT
 * desync side from action (the failure mode that motivated reading `side` first
 * for orders, which in turn mislabeled cross-framed buy-NOs as YES).
 *
 * SCOPE: orders only. FILLS keep the legacy `side`+`action` pair (outcome_side
 * flips on a sell fill, so realized-P&L sign relies on the legacy pair; fill
 * intent is reconciled server-side via the trades audit).
 *
 * CAVEAT: a genuine resting sell-YES-to-close is indistinguishable on the wire
 * from a buy-NO open (both `side:yes/action:sell/outcome_side:no`) and will
 * display as a NO. Desk closes are IOC sweeps that don't rest, so this is
 * acceptable; refine with live-position context if resting closes become common.
 */

import type { LegacySide, LegacyAction } from './orderV2';

export function reconstructOrderTraderSide(raw: {
  side?: unknown;
  action?: unknown;
  outcome_side?: unknown;
}): { side: LegacySide; action: LegacyAction } {
  const bookSide = raw.side === 'yes' || raw.side === 'no' ? raw.side : undefined;
  const outcomeSide =
    raw.outcome_side === 'yes' || raw.outcome_side === 'no' ? raw.outcome_side : undefined;
  // outcome_side is the trader-native side; fall back to legacy side only when
  // outcome_side is absent, and finally to 'yes' to preserve the prior default.
  const side: LegacySide = outcomeSide ?? bookSide ?? 'yes';
  const rawAction: LegacyAction = raw.action === 'sell' ? 'sell' : 'buy';
  const crossFramed = bookSide != null && outcomeSide != null && bookSide !== outcomeSide;
  const action: LegacyAction = crossFramed
    ? rawAction === 'buy'
      ? 'sell'
      : 'buy'
    : rawAction;
  return { side, action };
}
